
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AddProviderDialog } from "./AddProviderDialog";
import { EditProviderDialog } from "./EditProviderDialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, MapPin, Mail, Phone, Globe, EyeOff } from "lucide-react";

export const ProvidersManagement = () => {
  const { data: providers, isLoading, error } = useQuery({
    queryKey: ['admin-providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching providers:', error);
        throw error;
      }

      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading providers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading providers. Please try again.</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Providers Management</CardTitle>
          <AddProviderDialog />
        </div>
      </CardHeader>
      <CardContent>
        {providers && providers.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Custom Domain</TableHead>
                  <TableHead>Specialties</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Commission %</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((provider) => (
                  <TableRow key={provider.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        {provider.image_url ? (
                          <img
                            src={provider.image_url}
                            alt={provider.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {provider.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{provider.name}</p>
                            {provider.is_hidden && (
                              <Badge variant="outline" className="text-xs flex items-center gap-1">
                                <EyeOff className="h-3 w-3" />
                                Hidden
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">@{provider.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{provider.location}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(provider as any).custom_domain ? (
                        <div className="flex items-center space-x-1">
                          <Globe className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-700">{(provider as any).custom_domain}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {provider.specialties?.slice(0, 2).map((specialty, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {specialty}
                          </Badge>
                        ))}
                        {provider.specialties && provider.specialties.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{provider.specialties.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {provider.email && (
                          <div className="flex items-center space-x-1">
                            <Mail className="h-3 w-3 text-gray-400" />
                            <span className="text-xs">{provider.email}</span>
                          </div>
                        )}
                        {provider.phone && (
                          <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3 text-gray-400" />
                            <span className="text-xs">{provider.phone}</span>
                          </div>
                        )}
                        {provider.website && (
                          <div className="flex items-center space-x-1">
                            <Globe className="h-3 w-3 text-gray-400" />
                            <a 
                              href={provider.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Website
                            </a>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="font-medium">
                          {provider.rating ? provider.rating.toFixed(1) : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {provider.review_count || 0} reviews
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="font-medium">
                          {provider.commission_fee_percent ? `${provider.commission_fee_percent}%` : '2.90%'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <EditProviderDialog provider={provider} />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/provider/${provider.slug}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No providers found.</p>
            <AddProviderDialog />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
